import OpenPanelRight16 from '@carbon/icons-react/es/open-panel--right/16';
import { Dropdown, Menu } from 'antd';
import moment, { ISO_8601 } from 'moment';
import React, { Dispatch, FC, SetStateAction, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FormTemplate } from 'api/form/types';
import { ReactComponent as WebFormSVG } from 'assets/svgs/web-form.svg';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { RelativeTime } from 'components/atoms/relative-time';
import { Page } from 'components/molecules/page';
import {
  Caption01,
  HelperText01,
  ProductiveHeading05,
  ProductiveHeading07,
  Text02,
} from 'components/styles';
import { RoutePaths } from 'constant';
import { useGetUserFromIndexedDb } from 'hooks/use-idb';
import { useTranslation } from 'hooks/use-translation';
import { useFormTemplateUpdateMutation } from 'queries/form-template';
import { TemplateBreadcrump } from '../commons';

const Header = styled(Page.Header)`
  && {
    box-shadow: none;
    ${Page.Header.Title} {
      width: 60vw;
    }
  }
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;
const WrapperRightContent = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`;
const LastSavedText = styled.div`
  ${Caption01};
  ${Text02};
  color: var(--gray-80);
  margin-right: var(--layout-03);
`;
const PreviewAsButton = styled(Button)`
  height: 40px;
`;

const PreviewMenuIcon = styled.div`
  min-width: 40px;
  height: 40px;
  background: var(--warm-gray-10);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: var(--spacing-04);
`;

const PreviewMenu = styled(Menu)`
  padding: 0px;
`;
const PreviewMenuItem = styled(Menu.Item)`
  width: 288px;
  display: flex;
  white-space: initial;
  padding: var(--spacing-04);
  &:hover ${PreviewMenuIcon} {
    background: var(--blue-20);
  }
`;
const Separator = styled.div`
  margin: 0 var(--spacing-04);
  border-bottom: 1px solid var(--border-color);
`;

const PreviewMenuData = styled.div`
  display: flex;
  flex-direction: column;
`;
const PreviewMenuHeader = styled.div`
  ${ProductiveHeading07};
`;
const PreviewMenuMessage = styled.div`
  ${HelperText01};
  color: var(--gray-80);
  margin-top: var(--spacing-02);
`;

const MultiLineInput = styled(Input)`
  ${ProductiveHeading05};
  background: transparent;
  outline: none;
  padding-left: 0;
  width: 50%;
  border-color: transparent;

  &:focus {
    border-bottom-color: var(--gray-50);
  }
`;

const InputWrapper = styled.div`
  flex: 1;

  .ant-form-item {
    padding: 0;
  }

  .ant-form-item-has-error ${MultiLineInput} {
    &,
    &:hover,
    &:focus {
      background: transparent;
      border-bottom-color: var(--error-color);
      outline: none;
    }
  }
`;

const FormItem = styled(Form.Item)`
  margin: 0;
`;

interface Props {
  formTemplate: FormTemplate;
  setShowFormPane: Dispatch<SetStateAction<boolean>>;
}

export const FormPageHeader: FC<Props> = ({
  formTemplate,
  setShowFormPane,
}) => {
  const [form] = Form.useForm();

  const createdBy = useGetUserFromIndexedDb(formTemplate.updatedBy)?.firstName;
  const templatePlaceholderText = useTranslation(
    'templates.placeholders.template_name',
  );
  const { mutate } = useFormTemplateUpdateMutation(formTemplate.templateId);

  useEffect(() => {
    form.setFieldsValue({
      name: formTemplate.name,
    });
  }, [form, formTemplate.name]);

  const handleOnChange = (e) => {
    const latestValue = e.target.value;
    const old = formTemplate.name?.trim();

    if (latestValue !== old && latestValue) {
      mutate({ name: latestValue });
    }
  };

  return (
    <Header
      breadcrumbs={
        <TemplateBreadcrump
          route={RoutePaths.formTemplateRoot}
          id="templates.forms"
        />
      }
      title={
        <Wrapper>
          <Page.Header.Title>
            <InputWrapper>
              <Form form={form}>
                <FormItem
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter a template name',
                    },
                  ]}
                >
                  <MultiLineInput
                    data-cy="form-template.name"
                    onBlur={handleOnChange}
                    defaultValue={formTemplate.name}
                    placeholder={templatePlaceholderText}
                  />
                </FormItem>
              </Form>
            </InputWrapper>
          </Page.Header.Title>
          <WrapperRightContent>
            <LastSavedText data-cy="templates.last_saved_at">
              <FormattedMessage
                id="templates.last_saved_at"
                values={{
                  by: createdBy,
                  time: (
                    <RelativeTime
                      time={moment(formTemplate.updatedAt, ISO_8601)}
                    />
                  ),
                }}
              />
            </LastSavedText>

            <Dropdown
              overlay={
                <PreviewMenu>
                  <PreviewMenuItem
                    onClick={() =>
                      window.open(
                        `/form-preview/${formTemplate.templateId}`,
                        '_blank',
                      )
                    }
                    data-cy="form.preview.web"
                  >
                    <PreviewMenuIcon>
                      <WebFormSVG />
                    </PreviewMenuIcon>
                    <PreviewMenuData>
                      <PreviewMenuHeader>
                        <FormattedMessage id="form_templates.web_form" />
                      </PreviewMenuHeader>
                      <PreviewMenuMessage>
                        <FormattedMessage id="form_templates.web_form_message" />
                      </PreviewMenuMessage>
                    </PreviewMenuData>
                  </PreviewMenuItem>
                  <Separator />
                  <PreviewMenuItem
                    onClick={() => setShowFormPane(true)}
                    data-cy="form.preview.pane"
                  >
                    <PreviewMenuIcon>
                      <OpenPanelRight16 />
                    </PreviewMenuIcon>
                    <PreviewMenuData>
                      <PreviewMenuHeader>
                        <FormattedMessage id="form_templates.form_pane" />
                      </PreviewMenuHeader>
                      <PreviewMenuMessage>
                        <FormattedMessage id="form_templates.form_pane_message" />
                      </PreviewMenuMessage>
                    </PreviewMenuData>
                  </PreviewMenuItem>
                </PreviewMenu>
              }
              trigger={['hover']}
              placement="bottomRight"
            >
              <PreviewAsButton
                type="tertiary"
                size="small"
                data-cy="form-btn.preview.as"
              >
                <FormattedMessage id="preview_as" />
                ...
              </PreviewAsButton>
            </Dropdown>
          </WrapperRightContent>
        </Wrapper>
      }
    />
  );
};
